/**
 * 端末ローカルの設定（SPEC §7.6）。
 *
 * しきい値や「今回は除外」は世帯で共有する性質のものではなく、
 * サーバにも対応するテーブルが無いので IndexedDB の meta に置く（端末ごと）。
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback } from 'react';
import { DEFAULT_SHOPPING_THRESHOLD_DAYS } from '../../shared/calc';
import { db } from './db';

export interface Settings {
  /** 在庫切れ予測がこの日数以下なら買い物リストに載る（SPEC §6.4） */
  shoppingThresholdDays: number;
  /** ホームで在庫切れ間近を上部にピン留めする（SPEC §7.1） */
  pinUrgent: boolean;
  /** 「今回は除外」中の品目。購入を記録すると自動で外れる */
  excludedItemIds: string[];
  /** 買い物リストへ手動追加した品目 */
  manualItemIds: string[];
  /** 品目が 1 つも使っていないカテゴリも管理できるように保持する */
  extraCategories: string[];
}

export const DEFAULT_SETTINGS: Settings = {
  shoppingThresholdDays: DEFAULT_SHOPPING_THRESHOLD_DAYS,
  pinUrgent: true,
  excludedItemIds: [],
  manualItemIds: [],
  extraCategories: [],
};

const KEY = 'settings';

const normalize = (value: unknown): Settings => ({
  ...DEFAULT_SETTINGS,
  ...(typeof value === 'object' && value !== null ? (value as Partial<Settings>) : {}),
});

export async function readSettings(): Promise<Settings> {
  return normalize((await db.meta.get(KEY))?.value);
}

export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  await db.transaction('rw', db.meta, async () => {
    const current = normalize((await db.meta.get(KEY))?.value);
    await db.meta.put({ key: KEY, value: { ...current, ...patch } });
  });
}

export function useSettings(): [Settings, (patch: Partial<Settings>) => Promise<void>] {
  const stored = useLiveQuery(() => db.meta.get(KEY), []);
  const update = useCallback((patch: Partial<Settings>) => updateSettings(patch), []);
  return [normalize(stored?.value), update];
}

/** 購入を記録した品目は「今回は除外」「手動追加」から外す（買い物が済んだ扱い） */
export async function clearShoppingFlags(itemId: string): Promise<void> {
  const current = await readSettings();
  if (!current.excludedItemIds.includes(itemId) && !current.manualItemIds.includes(itemId)) return;
  await updateSettings({
    excludedItemIds: current.excludedItemIds.filter((id) => id !== itemId),
    manualItemIds: current.manualItemIds.filter((id) => id !== itemId),
  });
}
